import MDBox from "components/MDBox";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import MDTypography from "components/MDTypography";
import { InputLabel, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import HelpIcon from "@mui/icons-material/Help";
import { PlaceHolderTypes } from "constants/PlaceholderTypes";
import React, { useEffect, useRef } from "react";
import PlaceHolderSelect from "components/PlaceHolderSelect/PlaceHolderSelect";

interface WYSIWYGProps {
  title: string;
  content: string;
  tooltip?: string;
  type?: PlaceHolderTypes | null;
  disabled?: boolean;
  onChange?: (content?: string) => void;
  onBlur?: (event?: any, editor?: any) => void;
  config?: any;
  plantProductsCount?: number;
  hidePlaceholders?: string[];
}
// Styled box to add breakpoint styling
const MDBoxStyled = styled(MDBox)(({ theme }) => ({
  display: "flex",
  gap: 2,
  alignItems: "center",
  flexDirection: "column",
  marginBottom: "4px",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
}));

export default function WYSIWYG({
  title,
  content,
  onChange,
  tooltip,
  type,
  disabled = false,
  onBlur,
  config = {},
  plantProductsCount = 0,
  hidePlaceholders,
}: Partial<WYSIWYGProps>) {
  const editorRef = useRef<any>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      editorRef.current = null;
    };
  }, []);

  const isEditorUsable = (editorInstance?: any) =>
    !!editorInstance && !editorInstance.isDestroyed && !!editorInstance.model;

  return (
    <MDBox>
      <MDBoxStyled sx={title ? undefined : { mb: 0 }}>
        <MDBox sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {title && (
            <InputLabel
              sx={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em" }}
            >
              {title}
            </InputLabel>
          )}
          {tooltip && (
            <Tooltip
              title={
                <MDBox
                  dangerouslySetInnerHTML={{
                    __html: tooltip,
                  }}
                />
              }
            >
              <HelpIcon />
            </Tooltip>
          )}
        </MDBox>
        {type && !disabled && (
          <MDBox className="tour-proposal-placeholders" sx={{ marginLeft: "auto" }}>
            <PlaceHolderSelect
              type={type}
              hideKeys={hidePlaceholders}
              value=""
              disabled={disabled}
              sx={{ m: 0, my: 0 }}
              plantProductsCount={plantProductsCount}
              onChange={(e) => {
                const editor = editorRef.current;
                if (isEditorUsable(editor)) {
                  editor.model.change((writer) => {
                    editor.model.insertContent(writer.createText(e.target.value));
                  });
                }
              }}
            />
          </MDBox>
        )}
      </MDBoxStyled>
      <CKEditor
        data={content}
        onReady={(editor) => {
          if (!isMountedRef.current) {
            return;
          }
          editorRef.current = editor;
        }}
        disabled={disabled}
        editor={ClassicEditor}
        onChange={(event, editor) => {
          editorRef.current = editor;
          if (!isEditorUsable(editor)) {
            return;
          }
          onChange?.(editor.getData());
        }}
        onBlur={(event, editor) => {
          editorRef.current = editor;
          if (!isEditorUsable(editor)) {
            return;
          }
          onBlur?.(event, editor);
        }}
        config={config}
      />
    </MDBox>
  );
}
